import React from "react";
var __jsx = React.createElement;
import { injectA2HS } from '@monofe/app-common/a2hs';
import { injectStoresRenderer } from '@monofe/appstate';
import { injectOrBuildContainer } from '@monofe/di';
import { AwaitOnlineRender, DelayedRender } from '@monofe/ui-common';
import { injectDeleteTour, injectDownloadTour } from '@monofe/ui-workspace';
import LazyResolver from '@monofe/ui-workspace/common/LazyResolver';
import { injectDialog } from '@monofe/ui-workspace/dialog';
import { injectOfflineAlert } from '@monofe/ui-workspace/offline-alert';
import { injectToast } from '@monofe/ui-workspace/toast';
import { injectENV } from '@monofe/util';
import { injectServiceWorkerController } from '@monofe/worker';
import { AnimatePresence, LazyMotion, MotionConfig, domAnimation } from 'framer-motion';
import Head from 'next/head';
import { useCallback, useEffect } from 'react';
import '../../public/common.css';
import AnalyticsWrapper from '../components/AnalyticsWrapper';
import ContainerProvider from '../components/ContainerProvider';
import { log } from '../private';
import { injectSiteConfig } from '../utils/siteConfig';

const bindToast = () => import('@monofe/ui-workspace/toast');

const bindOfflineNotification = () => import('@monofe/ui-workspace/offline-alert');

const bindDialog = () => import('@monofe/ui-workspace/dialog');

const bindDownloadTour = () => import('@monofe/ui-workspace/download-tour');

const bindDeleteTour = () => import('@monofe/ui-workspace/delete-tour');

const bindA2HS = () => import('@monofe/app-common/a2hs');

const PATH_SERVICE_WORKER = 'service-worker.js';
const SCOPE_SERVICE_WORKER_DEFAULT = '.';
const ENV_IS_SW_ENABLED = 'REACT_APP_SW_ENABLED';
const ENV_SW_OVERRIDE = 'REACT_APP_SWFORCE';
const ENV_BUILDTYPE = 'NODE_ENV';

function startServiceWorker(container) {
  const env = injectENV(container);
  const isProduction = env.getString(ENV_BUILDTYPE) === 'production';
  /* For testing, run `yarn startsw` to force the Service Worker to run in development */

  const swEnableOverride = env.getBoolean(ENV_SW_OVERRIDE);
  const hasServiceWorker = ('serviceWorker' in navigator);
  const swEnabled = env.getBoolean(ENV_IS_SW_ENABLED, true);
  /* If missing assume YES, otherwise allow for ENV to disable service-workers (Agnelli) */

  if (swEnabled && hasServiceWorker && isProduction || swEnableOverride) {
    const controller = injectServiceWorkerController(container);
    /* Limit SW visibility to base subpath of PWA, scope *must* end with slash '/qc/mauto' -> '/qc/mauto/' */

    let scope = env.getString('REACT_APP_ROUTE_BASENAME') || new URL(document.baseURI).pathname
    /* Use <html base/> href */
    || SCOPE_SERVICE_WORKER_DEFAULT;

    if (!scope.endsWith('/')) {
      scope += '/';
    }

    controller.start(PATH_SERVICE_WORKER, scope);
  }

  return Promise.resolve();
}

const StoreProvider = _ref => {
  var _injectStoresRenderer;

  let {
    container,
    children
  } = _ref;
  const Render = (_injectStoresRenderer = injectStoresRenderer(container).render) !== null && _injectStoresRenderer !== void 0 ? _injectStoresRenderer : () => __jsx(React.Fragment, null);
  useEffect(() => {
    startServiceWorker(container).catch(err => {
      log.error('failed to start sw');
    });
  }, [container]);
  return __jsx(Render, null, children);
};

function useLazyBinder(container, binder, injectable, onCompletion) {
  return useCallback(() => new Promise((resolve, reject) => {
    binder().then(binder => binder.bind.bind(container)).then(() => {
      onCompletion === null || onCompletion === void 0 ? void 0 : onCompletion();
      resolve(injectable().renderer);
    }).catch(error => {
      reject(error);
    });
  }), [container, binder, injectable, onCompletion]);
}

function App(_ref2) {
  let {
    Component,
    pageProps,
    router
  } = _ref2;
  const container = injectOrBuildContainer();
  const siteConfig = injectSiteConfig();

  const updateA2HSSiteName = () => {
    injectA2HS(container).setSiteName(siteConfig.appLongName);
  };

  const LazyToast = useLazyBinder(container, bindToast, injectToast);
  const LazyOffline = useLazyBinder(container, bindOfflineNotification, injectOfflineAlert);
  const LazyDialog = useLazyBinder(container, bindDialog, injectDialog);
  const LazyDownloadTour = useLazyBinder(container, bindDownloadTour, injectDownloadTour);
  const LazyDeleteTour = useLazyBinder(container, bindDeleteTour, injectDeleteTour);
  const LazyA2HS = useLazyBinder(container, bindA2HS, injectA2HS, updateA2HSSiteName);
  return __jsx(React.Fragment, null, __jsx(Head, null, __jsx("meta", {
    name: "viewport",
    content: "width=device-width, height=device-height, initial-scale=1, minimum-scale=1, maximum-scale=1, viewport-fit=cover, user-scalable=no, minimal-ui"
  })), __jsx(ContainerProvider, null, __jsx(StoreProvider, {
    container: container
  }, __jsx(LazyMotion, {
    strict: true,
    features: domAnimation
  }, __jsx(MotionConfig, {
    transition: {
      duration: 0.3
    }
  }), __jsx(DelayedRender, {
    delay: 4000
  }, __jsx(AwaitOnlineRender, null, __jsx(LazyResolver, {
    resolvers: [LazyToast, LazyOffline, LazyDialog, LazyDownloadTour, LazyDeleteTour]
  }), __jsx(AnalyticsWrapper, {
    router: router
  }))), __jsx(DelayedRender, {
    delay: 30000
  }, __jsx(AwaitOnlineRender, null, __jsx(LazyResolver, {
    resolvers: [LazyA2HS]
  }))), __jsx(AnimatePresence, {
    key: "animated-switch",
    exitBeforeEnter: false,
    initial: false
  }, __jsx(Component, pageProps))))));
}

export default App;